/**
 * Shared mapping from survey API failures to terminal page states, used by
 * both the clinical and NPS survey flows so a new error reason only needs to
 * be handled in one place.
 * @agentic members
 */

import { SurveyError } from "./api";

/** Generic guidance shown on expired/invalid screens (no live phone lookup). */
export const CARE_TEAM_CONTACT_MESSAGE =
  "Please contact your care team to request a new survey link.";

/** Terminal states a failed survey request can resolve to. */
export type SurveyTerminalErrorState =
  | { status: "already-completed" }
  | { status: "expired" }
  | { status: "invalid" }
  | { status: "error"; message: string };

/**
 * Map a failed survey request to a terminal page state, distinguishing
 * already-completed, expired, and invalid links from generic errors.
 * @agentic members
 */
export const surveyErrorToState = (
  err: unknown,
  fallbackMessage: string,
): SurveyTerminalErrorState => {
  if (err instanceof SurveyError) {
    if (err.alreadyCompleted) return { status: "already-completed" };
    if (err.reason === "expired") return { status: "expired" };
    // A closed screener administration (inactivity past the same-session
    // window, D11) is terminal and lands on the same generic expired screen —
    // the server decided; the client only renders it.
    if (err.reason === "closed") return { status: "expired" };
    if (err.reason === "invalid") return { status: "invalid" };
  }
  return {
    status: "error",
    message: err instanceof Error ? err.message : fallbackMessage,
  };
};
